import moment from 'moment';
import { events } from '@/services/wss/SystemEvents';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import powerUsageMonthCache from './PowerUsageMonthCache';
import powerUsageMonthFetcher from './PowerUsageMonthFetcher';
import WssEventHandlerBase from '@/services/engines/WssEventHandlerBase';
import { PUM_REFRESH_MS } from './powerUsageMonthConst';

const _name = 'PowerUsageMonthEngine';

// BE end is exclusive and read as a local day key: tomorrow midnight includes today
const tomorrow = () => moment().add(1, 'day').startOf('day').valueOf();

class PowerUsageMonthEngine extends WssEventHandlerBase {
    _name = _name;
    _lastRefresh = 0;
    _timer = null;

    bootup() {
        super.bootup();
        this.clear();

        this._unsub = this._wsNotifier.subscribe(events.OnPowerSummaryUpdated, this.onMessage);
    }

    clear() {
        super.clear();

        if (this._unsub) this._unsub();
        clearTimeout(this._timer);
        this._timer = null;
    }

    /**
     * Day push: month totals come from BE, so refetch the current month (throttled).
     */
    onMessage = () => {
        try {
            if (this._timer) return;
            const wait = Math.max(0, this._lastRefresh + PUM_REFRESH_MS - Date.now());
            this._timer = setTimeout(this._refreshCurrentMonth, wait);
        } catch (error) {
            this.logError('onMessage', error.message);
        }
    };

    /**
     * @returns {Promise<object[]>} months from cache, fetches the year on a cache miss
     */
    getAll = async () => {
        try {
            const cached = powerUsageMonthCache.getAll();
            if (cached.length) return cached;

            await this.fetch();
            return powerUsageMonthCache.getAll();
        } catch (error) {
            this.logError('getAll', error.message);
            return [];
        }
    };

    /**
     * Loads 1 jan of the current year up to and including today.
     * @returns {Promise<object[]>}
     */
    fetch = () => this.refresh(moment().startOf('year').valueOf(), tomorrow());

    /**
     * Reloads months for the given range and upserts them by rk.
     * @param {number} start - epoch ms
     * @param {number} end - epoch ms, exclusive
     * @returns {Promise<object[]>}
     */
    refresh = async (start, end) => {
        try {
            const items = await powerUsageMonthFetcher.fetch(start, end);
            powerUsageMonthCache.addOrUpdate(items);
            this._notify(items);
            return items;
        } catch (error) {
            this.logError('refresh', error.message);
            return [];
        }
    };

    _refreshCurrentMonth = () => {
        this._timer = null;
        this._lastRefresh = Date.now();
        return this.refresh(moment().startOf('month').valueOf(), tomorrow());
    };

    // Carries only rks — consumers always re-read from powerUsageMonthCache.
    _notify = (items) => {
        ComponentNotifier.notify(_name, 'dataChanged', { rks: items.map((s) => s.rk) });
    };
}

const powerUsageMonthEngine = new PowerUsageMonthEngine();
export default powerUsageMonthEngine;
